import React, { useState, useEffect } from 'react';
import { Navbar } from '../../components/feature/Navbar';
import { Footer } from '../../components/feature/Footer';
import { isSuspiciousSubmission, addTrackingToForm } from '../../utils/tracking';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    honeypot: '', // Honeypot field
    mathAnswer: '' // Math verification
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [mathQuestion, setMathQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
  const [formErrors, setFormErrors] = useState({
    email: ''
  });

  // Fake/disposable email domains to block
  const blockedDomains = [
    'test.com', 'fake.com', 'example.com', 'temp-mail.com', 'tempmail.com',
    'throwaway.email', '10minutemail.com', 'guerrillamail.com', 'mailinator.com',
    'trashmail.com', 'yopmail.com', 'fakeinbox.com', 'maildrop.cc'
  ];

  // Stricter email validation
  const validateEmail = (email: string): { valid: boolean; error: string } => {
    // Basic format check
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!emailRegex.test(email)) {
      return { valid: false, error: 'Please enter a valid email address' };
    }

    // Extract domain
    const domain = email.split('@')[1].toLowerCase();

    // Check against blocked domains
    if (blockedDomains.includes(domain)) {
      return { valid: false, error: 'Please use a valid business or personal email address' };
    }

    // Check for common typos
    const commonTypos: Record<string, string> = {
      'gmial.com': 'gmail.com',
      'gmai.com': 'gmail.com',
      'yahooo.com': 'yahoo.com',
      'yaho.com': 'yahoo.com',
      'hotmial.com': 'hotmail.com',
      'outlok.com': 'outlook.com'
    };

    if (commonTypos[domain]) {
      return { valid: false, error: `Did you mean ${email.split('@')[0]}@${commonTypos[domain]}?` };
    }

    return { valid: true, error: '' };
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFormData({ ...formData, email });

    // Validate email on change
    if (email) {
      const validation = validateEmail(email);
      setFormErrors({ ...formErrors, email: validation.error });
    } else {
      setFormErrors({ ...formErrors, email: '' });
    }
  };

  useEffect(() => {
    // Add Google Fonts
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);

    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);

    const fontLink = document.createElement('link');
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@700;800&family=Dancing+Script:wght@400;700&display=swap';
    fontLink.rel = 'stylesheet';
    document.head.appendChild(fontLink);

    // Update canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', 'https://businesswebsitepro.com/contact');

    document.title = 'Contact BusinessWebsitePro | Free Consultation, No Obligation | Vancouver, BC Canada | BWP';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Get your free consultation with no obligation. Contact BusinessWebsitePro in Vancouver, BC for transparent website design services. Email or Telegram response within 24 hours.');
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Contact BusinessWebsitePro | Free Consultation, No Obligation | Vancouver, BC Canada | BWP');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Get your free consultation with no obligation. Contact BusinessWebsitePro in Vancouver, BC for transparent website design services. Email or Telegram response within 24 hours.');
    }

    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', 'Contact BusinessWebsitePro | Free Consultation, No Obligation | Vancouver, BC Canada | BWP');
    }

    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', 'Get your free consultation with no obligation. Contact BusinessWebsitePro in Vancouver, BC for transparent website design services. Email or Telegram response within 24 hours.');
    }

    // Generate random math question
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setMathQuestion({ num1, num2, answer: num1 + num2 });

    return () => {
      document.head.removeChild(preconnect1);
      document.head.removeChild(preconnect2);
      document.head.removeChild(fontLink);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Bot protection checks
    // 1. Honeypot check - if filled, it's a bot
    if (formData.honeypot) {
      console.log('Bot detected via honeypot');
      setIsSubmitting(false);
      return;
    }

    // 2. Math verification check
    if (parseInt(formData.mathAnswer) !== mathQuestion.answer) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      alert('Incorrect answer to the math question. Please try again.');
      return;
    }

    // 3. Time-based bot detection - Block submissions under 60 seconds
    const suspicionCheck = isSuspiciousSubmission();
    if (suspicionCheck.suspicious) {
      setIsSubmitting(false);
      alert(suspicionCheck.reason);
      return;
    }

    // Validate email before submission
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) {
      setFormErrors({ ...formErrors, email: emailValidation.error });
      setIsSubmitting(false);
      return;
    }

    try {
      const formElement = e.target as HTMLFormElement;
      
      // Add tracking data to form
      await addTrackingToForm(formElement);
      
      const formDataToSend = new FormData(formElement);
      
      // Remove honeypot and math answer from submission
      formDataToSend.delete('honeypot');
      formDataToSend.delete('mathAnswer');

      // Send to form storage
      const formResponse = await fetch('https://readdy.ai/api/form/d4qahv2r20r5k3vfkofg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      // Send to Telegram
      const telegramData: any = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
      };

      // Add tracking fields from form
      const trackingFields = formElement.querySelectorAll('[data-tracking-field]');
      trackingFields.forEach((field: any) => {
        telegramData[field.name] = field.value;
      });

      const telegramResponse = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/telegram-notify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(telegramData)
      });

      if (formResponse.ok || telegramResponse.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          honeypot: '',
          mathAnswer: ''
        });
        
        // Generate new math question
        const num1 = Math.floor(Math.random() * 10) + 1;
        const num2 = Math.floor(Math.random() * 10) + 1;
        setMathQuestion({ num1, num2, answer: num1 + num2 });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Schema.org ContactPage structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact BusinessWebsitePro",
          "description": "Contact us for professional website design services in Vancouver and Canada",
          "url": "https://businesswebsitepro.com/contact",
          "mainEntity": {
            "@type": "LocalBusiness",
            "name": "BusinessWebsitePro",
            "telephone": "+16048077862",
            "email": "info@businesswebsitepro.com",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Vancouver",
              "addressRegion": "BC",
              "addressCountry": "CA"
            }
          }
        })}
      </script>
      
      {/* Hero Section */}
      <section className="relative h-[75vh] overflow-hidden mt-20">
        {/* Hero Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/afbb870fba5009b6251518206686e578/9f211590c03760b277a488f14cddc141.png"
            alt="Contact us"
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Brand Text - Top Right */}
        <div className="absolute top-24 right-6 sm:right-10 z-20 max-w-[600px] text-right">
          <h1 className="text-3xl sm:text-4xl md:text-4xl font-extrabold text-white drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)] whitespace-nowrap" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Business Website Pro
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl text-white drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)] mt-2" style={{ fontFamily: 'Dancing Script, cursive' }}>
            Innovative Design. No Hidden Fees.
          </p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Contact Form */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Send Us a Message</h2>
              
              {/* Call Back Notice */}
              <div className="mb-8 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Prefer a Phone Call?</h3>
                    <p className="text-sm text-gray-700">
                      Include your phone number in the message, and we'll call you back during business hours (9 AM - 6 PM PST).
                    </p>
                  </div>
                </div>
              </div>
              
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#0d3b66] focus:outline-none text-base transition-colors"
                    placeholder="John Smith"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleEmailChange}
                    required
                    className={`w-full px-6 py-4 rounded-xl border-2 focus:outline-none text-base transition-colors ${
                      formErrors.email ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-[#0d3b66]'
                    }`}
                    placeholder="john@example.com"
                  />
                  {formErrors.email && (
                    <p className="text-xs text-red-600 mt-1 flex items-center">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formErrors.email}
                    </p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number <span className="text-blue-600">(Include if you'd like us to call you)</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#0d3b66] focus:outline-none text-base transition-colors"
                    placeholder="+1 (604) 123-4567"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#0d3b66] focus:outline-none text-base transition-colors resize-none"
                    placeholder="Tell us about your project..."
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-2">{formData.message.length}/500 characters</p>
                </div>
                
                {/* Honeypot field - hidden from users, only bots will fill it */}
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={handleInputChange}
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                  tabIndex={-1}
                  autoComplete="off"
                />

                {/* Math Verification */}
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
                  <label htmlFor="mathAnswer" className="block text-sm font-semibold text-gray-900 mb-3">
                    <i className="ri-shield-check-line text-yellow-600 mr-2"></i>
                    Security Check: What is {mathQuestion.num1} + {mathQuestion.num2}? *
                  </label>
                  <input
                    type="number"
                    id="mathAnswer"
                    name="mathAnswer"
                    value={formData.mathAnswer}
                    onChange={handleInputChange}
                    required
                    className="w-full md:w-32 px-4 py-3 border-2 border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm font-semibold"
                    placeholder="Answer"
                  />
                  <p className="text-xs text-gray-600 mt-2">
                    This helps us prevent spam and ensure you're a real person
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0d3b66] text-white py-5 rounded-xl text-lg font-semibold hover:bg-[#0a2d4d] transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 flex items-center gap-3">
                    <i className="ri-check-line text-2xl text-green-600"></i>
                    <p className="text-green-800 font-semibold">Message sent successfully! We'll respond within 24 hours.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 flex items-center gap-3">
                    <i className="ri-error-warning-line text-2xl text-red-600"></i>
                    <p className="text-red-800 font-semibold">Something went wrong. Please try again.</p>
                  </div>
                )}
              </form>
              
              <p className="text-sm text-gray-500 text-center mt-6">
                We'll respond within 24 hours
              </p>
            </div>
            
            {/* Right Column - Contact Information */}
            <div className="space-y-6">
              {/* WhatsApp Card */}
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center mb-4">
                  <i className="ri-whatsapp-line text-2xl text-white"></i>
                </div>
                <p className="text-xs uppercase tracking-wider text-gray-600 mb-2">WHATSAPP</p>
                <a href="https://wa.me/16048077862" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#25D366] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#20BA5A] transition-colors cursor-pointer whitespace-nowrap">
                  Message on WhatsApp
                </a>
                <p className="text-sm text-gray-600 mt-3">Send us a message anytime</p>
              </div>

              {/* Telegram Card */}
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="w-12 h-12 bg-[#0088cc] rounded-full flex items-center justify-center mb-4">
                  <i className="ri-telegram-line text-2xl text-white"></i>
                </div>
                <p className="text-xs uppercase tracking-wider text-gray-600 mb-2">TELEGRAM</p>
                <a href="https://t.me/businesswebsitepro_bot" target="_blank" rel="noopener noreferrer" className="text-xl font-bold text-[#0d3b66] hover:text-[#0a2d4d] transition-colors cursor-pointer break-all">
                  @businesswebsitepro_bot
                </a>
                <p className="text-sm text-gray-600 mt-3">Chat with us instantly</p>
              </div>
              
              {/* Email Card */}
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="w-12 h-12 bg-[#0d3b66] rounded-full flex items-center justify-center mb-4">
                  <i className="ri-mail-line text-2xl text-white"></i>
                </div>
                <p className="text-xs uppercase tracking-wider text-gray-600 mb-2">EMAIL</p>
                <a href="mailto:info@businesswebsitepro.com" className="text-xl font-bold text-[#0d3b66] hover:text-[#0a2d4d] transition-colors cursor-pointer break-all" itemProp="email">
                  info@businesswebsitepro.com
                </a>
                <p className="text-sm text-gray-600 mt-3">We respond within 24 hours</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block bg-blue-100 text-[#0d3b66] text-sm font-semibold px-4 py-2 rounded-full mb-4">
              WHERE WE SERVE
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Service Area</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Providing professional website design services across Canada, United States, and worldwide
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Canada Cities */}
            <div className="bg-blue-50 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-3xl text-[#0d3b66]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">British Columbia</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Vancouver</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Victoria</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Surrey</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Burnaby</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Richmond</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Kelowna</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-3xl text-[#0d3b66]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Ontario</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Toronto</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Ottawa</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Mississauga</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Brampton</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Hamilton</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>London</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-3xl text-[#0d3b66]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Alberta & More</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Calgary</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Edmonton</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Montreal, QC</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Winnipeg, MB</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Halifax, NS</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Saskatoon, SK</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-global-line text-3xl text-[#0d3b66]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">United States & Global</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>New York, NY</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Los Angeles, CA</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Chicago, IL</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-arrow-right-s-line text-[#0d3b66]"></i>
                  <span>Seattle, WA</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-earth-line text-[#0d3b66]"></i>
                  <span className="font-semibold">Worldwide Service</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-gray-600">
              Don't see your city? <strong className="text-[#0d3b66]">We serve clients worldwide!</strong> Contact us to discuss your project.
            </p>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-gray-100" aria-label="Vancouver location map">
        <div className="w-full h-96">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d83327.61346126429!2d-123.19934205!3d49.26038635!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x548673f143a94fb3%3A0xbb9196ea9b81f38b!2sVancouver%2C%20BC%2C%20Canada!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Vancouver Location Map"
          ></iframe>
        </div>
      </section>

      {/* Social Section */}
      <section className="py-24 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Connect With Us</h2>
          <nav className="flex items-center justify-center gap-6" aria-label="Social media integration">
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer nofollow" className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center hover:bg-[#0d3b66] hover:scale-110 transition-all duration-300 cursor-pointer group" aria-label="Facebook">
              <i className="ri-facebook-fill text-3xl text-[#0d3b66] group-hover:text-white"></i>
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer nofollow" className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center hover:bg-[#0d3b66] hover:scale-110 transition-all duration-300 cursor-pointer group" aria-label="Instagram">
              <i className="ri-instagram-line text-3xl text-[#0d3b66] group-hover:text-white"></i>
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer nofollow" className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center hover:bg-[#0d3b66] hover:scale-110 transition-all duration-300 cursor-pointer group" aria-label="LinkedIn">
              <i className="ri-linkedin-fill text-3xl text-[#0d3b66] group-hover:text-white"></i>
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer nofollow" className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center hover:bg-[#0d3b66] hover:scale-110 transition-all duration-300 cursor-pointer group" aria-label="Twitter">
              <i className="ri-twitter-x-line text-3xl text-[#0d3b66] group-hover:text-white"></i>
            </a>
            <a href="https://youtube.com" target="_blank" rel="noopener noreferrer nofollow" className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center hover:bg-[#0d3b66] hover:scale-110 transition-all duration-300 cursor-pointer group" aria-label="YouTube">
              <i className="ri-youtube-fill text-3xl text-[#0d3b66] group-hover:text-white"></i>
            </a>
          </nav>
        </div>
      </section>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-2xl">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <i className="ri-check-line text-4xl text-green-600"></i>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Message Sent!</h3>
            <p className="text-lg text-gray-600 mb-8">
              Thank you for contacting us. We'll get back to you within 24 hours.
            </p>
            <button
              onClick={() => setShowConfirmation(false)}
              className="bg-[#0d3b66] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#0a2d4d] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}